import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import type { AuditLog } from '@src/models/interfaces/AuditLog/AuditLogInterface';
import AuditLogService from '@src/services/AuditLogService';

interface UseAuditLogsOptions {
  enabled?: boolean;
  entityType?: string;
  entityId?: string;
  initialPageSize?: number;
}

export interface UseAuditLogsReturn {
  logs: AuditLog[];
  total: number;
  loading: boolean;
  error: Error | null;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  search: (value: string) => Promise<void>;
  changePage: (page: number, size?: number) => Promise<void>;
  retry: () => Promise<void>;
  reset: () => void;
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Unable to load audit logs');

export const useAuditLogs = ({
  enabled = true,
  entityType,
  entityId,
  initialPageSize = 20,
}: UseAuditLogsOptions = {}): UseAuditLogsReturn => {
  const orgId = useSelector(
    ({ organizations }: CommonReduxState) => organizations.organization.id,
  );
  const serviceRef = useRef<AuditLogService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new AuditLogService();
  }
  const auditLogService = serviceRef.current;
  // Guards against an older in-flight request overwriting state after a newer one
  // (rapid search/pagination) or after the viewer was closed and reset.
  const requestIdRef = useRef(0);

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(
    async (page: number, size: number, searchValue: string): Promise<void> => {
      if (!enabled || !orgId) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      const isStale = (): boolean => requestId !== requestIdRef.current || !enabled;

      setLoading(true);
      setError(null);
      try {
        const response = await auditLogService.all({
          orgId,
          entityTypes: entityType ? [entityType] : [],
          entityIds: entityId ? [entityId] : [],
          pageSize: size,
          lastPosition: (page - 1) * size,
          searchTerm: searchValue,
        });
        if (isStale()) return;
        setLogs(response.logs);
        setTotal(response.total);
      } catch (loadError: unknown) {
        if (isStale()) return;
        setLogs([]);
        setTotal(0);
        setError(toError(loadError));
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    },
    [auditLogService, enabled, entityId, entityType, orgId],
  );

  useEffect(() => {
    if (enabled) {
      void load(1, initialPageSize, '');
    }
  }, [enabled, initialPageSize, load]);

  const search = useCallback(
    async (value: string): Promise<void> => {
      setSearchTerm(value);
      setCurrentPage(1);
      await load(1, pageSize, value);
    },
    [load, pageSize],
  );

  const changePage = useCallback(
    async (page: number, size: number = pageSize): Promise<void> => {
      const pageChangedBySize = size !== pageSize;
      const nextPage = pageChangedBySize ? 1 : page;
      setCurrentPage(nextPage);
      setPageSize(size);
      await load(nextPage, size, searchTerm);
    },
    [load, pageSize, searchTerm],
  );

  const retry = useCallback(
    async (): Promise<void> => load(currentPage, pageSize, searchTerm),
    [currentPage, load, pageSize, searchTerm],
  );

  const reset = useCallback((): void => {
    requestIdRef.current += 1;
    setLogs([]);
    setTotal(0);
    setLoading(false);
    setError(null);
    setCurrentPage(1);
    setPageSize(initialPageSize);
    setSearchTerm('');
  }, [initialPageSize]);

  return {
    logs,
    total,
    loading,
    error,
    currentPage,
    pageSize,
    searchTerm,
    search,
    changePage,
    retry,
    reset,
  };
};
